import React, { useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import useExerciseEngine from "@/hooks/useExerciseEngine";

const BLACK_CELL = "#";

export default function CrosswordExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= DATA ================= */
  const crosswordData =
    exercise.crosswordData || { grid: [], clues: { across: [], down: [] } };

  const grid = crosswordData.grid || [];
  const gridSize = grid.length;

  const clues = {
    across: crosswordData.clues?.across || [],
    down: crosswordData.clues?.down || [],
  };

  /* ================= FLATTEN QUESTIONS FOR ENGINE ================= */
  const questions = [];

  grid.forEach((row, rIdx) => {
    row.forEach((cell, cIdx) => {
      if (cell && cell !== BLACK_CELL) {
        questions.push({
          type: "crossword",
          key: `${rIdx}-${cIdx}`,
          answer: cell,
          skill: "spelling",
        });
      }
    });
  });

  /* ================= ENGINE ================= */
 const {
  answers,
  setAnswer,
  submitIfReady,
  getInputState,
} = useExerciseEngine({
  questions,
  showResult,
  onComplete,
  externalAnswers: userAnswer || {}, 
});

  

  /* ================= EDIT MODE ================= */
  const updateCrossword = (next) => {
    if (isEditMode) onUpdate("crosswordData", next);
  };

  const createGrid = (size) => {
    updateCrossword({
      grid: Array.from({ length: size }, () =>
        Array.from({ length: size }, () => "")
      ),
      clues: { across: [], down: [] },
    });
  };

  const handleCellChange = (r, c, value) => {
    const letter =
      value === "#"
        ? BLACK_CELL
        : value.slice(0, 1).toUpperCase();

    const nextGrid = grid.map((row, ri) =>
      row.map((cell, ci) =>
        ri === r && ci === c ? letter : cell
      )
    );

    updateCrossword({ ...crosswordData, grid: nextGrid });
  };

  if (isEditMode) {
    return (
      <div className="space-y-10">
        {gridSize === 0 && (
          <div className="text-center py-10">
            <Button onClick={() => createGrid(10)}>
              Create 10×10 Grid
            </Button>
          </div>
        )}

        {gridSize > 0 && (
          <div className="flex justify-center">
            <div
              className="grid gap-1 bg-muted/50 p-4 rounded-xl"
              style={{
                gridTemplateColumns: `repeat(${gridSize}, 48px)`,
              }}
            >
              {grid.map((row, rIdx) =>
                row.map((cell, cIdx) => (
                  <Input
                    key={`${rIdx}-${cIdx}`}
                    maxLength={1}
                    value={cell === BLACK_CELL ? "#" : cell}
                    onChange={(e) =>
                      handleCellChange(rIdx, cIdx, e.target.value)
                    }
                    className={cn(
                      "w-12 h-12 text-center font-bold",
                      cell === BLACK_CELL && "bg-black text-white"
                    )}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (gridSize === 0) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No crossword available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT ================= */
  return (
    <Card className="max-w-5xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Crossword Puzzle</CardTitle>
        <CardDescription>
          Fill in the crossword using clues
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-10">
        {/* GRID */}
        <div className="flex justify-center">
          <div
            className="grid gap-0.5 bg-muted/60 p-4 rounded-xl"
            style={{
              gridTemplateColumns: `repeat(${gridSize}, 50px)`,
            }}
          >
            {grid.map((row, rIdx) =>
              row.map((cell, cIdx) => {
                const isBlack = cell === BLACK_CELL;
                const key = `${rIdx}-${cIdx}`;
                const value = answers[key] || "";

                const state = getInputState(value, cell);

                return (
                  <div key={key}>
                    {isBlack ? (
                      <div className="w-12 h-12 bg-black" />
                    ) : (
                      <Input
                        maxLength={1}
                        value={value}
                        disabled={showResult}
                        onChange={(e) => {
                          const letter = e.target.value
                            .slice(-1)
                            .toUpperCase();

                          setAnswer(key, letter);

                          submitIfReady();
                        }}
                        className={cn(
                          "w-12 h-12 text-center text-xl font-bold uppercase",
                          state.className
                        )}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* CLUES */}
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold">Across</h3>
            {clues.across.map((c) => (
              <p key={c.number}>
                {c.number}. {c.clue}
              </p>
            ))}
          </div>

          <div>
            <h3 className="font-semibold">Down</h3>
            {clues.down.map((c) => (
              <p key={c.number}>
                {c.number}. {c.clue}
              </p>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}